(() => {
  if (customElements.get('bundle-product')) {
    return;
  }
 
  class BundleProduct extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        bundleCollection: '[data-bundle-collection]',
        bundleCard: '[data-bundle-card]',
        addonProduct: '[data-bundle-addon]',
        wrapper: 'data-island.bundle-product',
        sellingPlans: '[data-selling-plan-group]',
        oneTime: '[data-one-time]',
        buyButton: '[data-buy-button]',
        mobileSlideOuts: 'mobile-slide-out',
        summary: '[data-bundle-summary]',
        items: '[data-bundle-item]',
        bundlePriceContainer: '[data-bundle-price]',
        bundlePriceBase: '[data-bundle-price-base]',
        bundlePriceCurrent: '[data-bundle-price-current]',
      }
      this.rechargeData = null;
      this.requiredQuantity = null;
      this.buyButtonEnabled = false;
      this.handle = this.getAttribute('data-handle');
      this.variantId = this.getAttribute('data-variant-id');
      this.productId = this.getAttribute('data-product-id');
      this.isDynamic = this.hasAttribute('data-dynamic');
      this.additionalFlatDiscount = Number(this.getAttribute('data-additional-flat-discount')) || 0;
      this.errorMessageContainer = this.querySelector('[data-bundle-error-messages]');

      this.selectedPlan = null;
    }

    get selectedBundleCards() {
      const bundleCards = this.wrapper.querySelectorAll(`${this.selectors.bundleCard}.is-selected`);
      return [...bundleCards].map(bundleCard => {
        return bundleCard.getBundleData();
      })
    }

    get selectedAddonProducts() {
      const addonProducts = this.wrapper.querySelectorAll(`${this.selectors.addonProduct}.is-selected`);
      return [...addonProducts].map(addonProduct => {
        return addonProduct.getBundleData();
      })
    }

    get openedSlideOuts() {
      return [...this.mobileSlideOuts].filter(slideOut => {
        return slideOut.classList.contains('has-been-opened');
      })
    }

    /**
     * Retrieves an array of addon product variant IDs from the checked elements
     * within the wrapper. Each ID is converted to a number.
     *
     * @getter
     * @returns {number[]} An array of addon product variant IDs.
     */
    get addonProductsIds() {
      const addonProductsIds = Array.from(
        this.wrapper.querySelectorAll('[data-addon-product-variant-id]:checked')
      ).map(el => Number(el.getAttribute('data-addon-product-variant-id')));
      return addonProductsIds;
    }

    /**
     * Handle slide out open.
     */
    handleSlideOutOpen() {
      const allSlidesOutsOpened = this.openedSlideOuts.length === this.mobileSlideOuts.length;

      if (!allSlidesOutsOpened) {
        return;
      }

      this.toggleBuyButton();
    }
     
    /**
     * Toggle buy button.
     */
    toggleBuyButton() {
      const totalQuantity = this.selectedBundleCards.reduce((acc, bundleCard) => {
        return acc + bundleCard.quantity;
      }, 0);
      
      let quantityConditionMet = true;

      const quantityRange = this.rechargeData?.variants?.[0]?.ranges?.[0];

      if (quantityRange) {
        const minRequiredQuantity = quantityRange.quantity_min !== null ? quantityRange.quantity_min : 0;
        const maxAllowedQuantity = quantityRange.quantity_max !== null ? quantityRange.quantity_max : Infinity;
        quantityConditionMet = totalQuantity >= minRequiredQuantity && totalQuantity <= maxAllowedQuantity;
      } else {
        quantityConditionMet = this.requiredQuantity !== 0 ? totalQuantity === this.requiredQuantity : true;
      }

      const collectionAreValid = [...this.bundleCollections].every(bundleCollection => {
        return bundleCollection.isValid;
      });
      
      this.buyButtonEnabled = quantityConditionMet && collectionAreValid;
      
      this.buyButtons.forEach(button => {
        button.disabled = !this.buyButtonEnabled;
        button.querySelector('[data-atc-text]').classList.toggle('hidden', !this.buyButtonEnabled);
        button.querySelector('[data-select-text]').classList.toggle('hidden', this.buyButtonEnabled);
      })
    }

    /**
     * Handle quantity update.
     */
    handleQuantityUpdate() {
      this.toggleBuyButton();
      this.updateSummaryInfo();
      this.updateErrorMessages();
      this.updateBundleCardsData();
    }

    updateErrorMessages() {
      this.errorMessageContainer.innerHTML = [...this.bundleCollections].map(bundleCollection => {
        // Check if valid first
        if (bundleCollection.isValid && this.buyButtonEnabled) {
          return '';
        }

        // Access the DATA ATTRIBUTE instead of a property
        const message = bundleCollection.getAttribute('data-error-message');

        if (!message || message === '') {
          return '';
        }

        return `<p class="bundle-product__error-message">${message}</p>`;
      }).join('').trim();
    }


    /**
     * Handles the change event for add-ons.
     * This method updates the summary information whenever an add-on is changed.
     */
    handleAddonChange(e) {
      this.updateSummaryInfo();
    }

    /**
     * Updates the summary information for the bundle product by calculating 
     * the base and current prices based on selected addons and collections.
     * It iterates through the items, updating the summary display and 
     * recalculating prices accordingly.
     *
     * @returns {void} This function does not return a value.
     */
    updateSummaryInfo() {
      // Update addons and collections in summary
      let baseAddonPrice = 0;
      let currentAddonPrice = 0;
      let baseCollectionPrice = 0;
      let currentCollectionPrice = 0;

      for (const item of this.items) {
        const itemType = item.getAttribute('data-bundle-item');

        switch (itemType) {
          case 'addon':
            const { price, discountedPrice, productId, title } = item.getBundleData();
            const addonSummary = this.summary.querySelector(`[data-bundle-addon="${productId}"]`);
            const isDiscounted = discountedPrice !== price;

            if (!item.querySelector('input:checked')) {
              addonSummary.innerHTML = '';
              break;
            }

            baseAddonPrice += price;
            currentAddonPrice += discountedPrice || price;

           addonSummary.innerHTML = `
              <p>${title}</p>
              <p>
                ${isDiscounted ? `<span class='bundle-product__details-text-price-current'>${formatMoney(discountedPrice, theme.moneyFormat)}</span>` : ''}
                <span class='bundle-product__details-text-price-base'>${formatMoney(price, theme.moneyFormat)}</span>
              </p>   
            `;
            break;
        
          case 'collection':
            const collectionId = item.getAttribute('data-collection-id');
            const collectionSummary = this.summary.querySelector(`[data-collection-id="${collectionId}"]`);
            const collectionSelectedCards = this.selectedBundleCards.filter(bundleCard => {
              return bundleCard.collection === collectionId;
            });

            collectionSummary.innerHTML = '';

            for (const bundleCard of collectionSelectedCards) {
              const { quantity, title, price, discountedPrice } = bundleCard;
              const isDiscounted = discountedPrice !== price;
              
              if (quantity <= 0) {
                continue;
              }

              baseCollectionPrice += price;
              currentCollectionPrice += discountedPrice || price;

              collectionSummary.innerHTML += `
                <p class="bundle-product__details-text">
                  <span>${title} x${quantity}</span>
      
                  <span>
                    ${isDiscounted ? `<span class='bundle-product__details-text-price-current'>${formatMoney(discountedPrice, theme.moneyFormat)}</span>` : ''}
                    <span class='bundle-product__details-text-price-base'>${formatMoney(price, theme.moneyFormat)}</span>
                  </span>  
                </p>
              `
            }

            break;

          default:
            break;
        }
      }

      // Update prices
      const basePriceElements = this.wrapper.querySelectorAll(this.selectors.bundlePriceBase);
      const currentPriceElement = this.wrapper.querySelector(this.selectors.bundlePriceCurrent);

      const totalBasePrice = baseAddonPrice + baseCollectionPrice;
      const totalCurrentPrice = Math.max(currentAddonPrice + currentCollectionPrice - this.additionalFlatDiscount, 0);

      if (totalBasePrice === 0) {
        this.querySelectorAll('[data-hide-if-empty]').forEach(el => el.classList.add('hidden'));
      } else {
        this.querySelectorAll('[data-hide-if-empty]').forEach(el => el.classList.remove('hidden'));
      }

      if (basePriceElements && totalBasePrice !== totalCurrentPrice) {
        basePriceElements.forEach(el => el.querySelector('span').innerHTML = formatMoney(totalBasePrice, theme.moneyFormat));
      } else {
        basePriceElements.forEach(el => el.querySelector('span').innerHTML = '');
      }

      if (currentPriceElement) {
        currentPriceElement.querySelector('[data-price]').innerHTML = formatMoney(totalCurrentPrice, theme.moneyFormat);
      }

      if (baseCollectionPrice > 0) {
        const collectionPriceContainer = this.querySelector('[data-bundle-price-plan]');
        const baseCollectionPriceElement = collectionPriceContainer.querySelector('del');
        const currentCollectionPriceElement = collectionPriceContainer.querySelector('ins');

        if (baseCollectionPriceElement && baseCollectionPrice !== currentCollectionPrice) {
          baseCollectionPriceElement.innerHTML = formatMoney(baseCollectionPrice, theme.moneyFormat);
        } else {
          baseCollectionPriceElement.innerHTML = '';
        }

        if (currentCollectionPriceElement) {
          currentCollectionPriceElement.innerHTML = formatMoney(currentCollectionPrice, theme.moneyFormat); 
        }

        this.querySelectorAll('[data-hide-if-no-recurring]').forEach(el => el.classList.remove('hidden'));
      } else {
        this.querySelectorAll('[data-hide-if-no-recurring]').forEach(el => el.classList.add('hidden'));
      }
    }

    /**
     * Get recharge data.
     */
    getRechargeData() {
      fetch(`/tools/recurring/bundle-data/${window.__st.rid}`)
        .then((response) => response.json())
        .then((data) => {
          this.rechargeData = data.bundle_settings;
          this.requiredQuantity = data.bundle_settings.variants[0].items_count || 0;
          [...this.bundleCollections].forEach(bundleCollection => {
            bundleCollection.applyQuantityRules(this.rechargeData)
          });
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    }

    /**
     * Handle plan change.
     * 
     * @param {Event} event?
     * @returns {Void}
     */
    handlePlanChange(event) {
      event?.preventDefault();
      const sellingPlanElement = this.querySelector('input:checked').closest(this.selectors.sellingPlans);

      if (!sellingPlanElement) {
        // One time purchase

        this.sellingPlan = null;
        this.sellingPlanGroupId = null;
        this.updateBundleCardsData();
        return;
      }
      
      const selectedOption = sellingPlanElement.querySelector('select[js-selling-plan-select] option:checked');
      this.sellingPlanGroupId = selectedOption.getAttribute('data-selling-plan-group-id');
      this.sellingPlan = sellingPlanElement.querySelector('select[js-selling-plan-select]').value;
      this.updateBundleCardsData();
    }

    updateBundleCardsData() {
      this.bundleCardsData = this.selectedBundleCards.map((bundleCard, index) => {
        const sellingPlan = bundleCard.sellingPlans.sellingPlanGroups.find((group) => {
          return group && group.id === this.sellingPlanGroupId;
        })?.selling_plans[0] || null;

        return {
          id: Number(bundleCard.variantId),
          selling_plan: sellingPlan?.id || null,
          quantity: bundleCard.quantity
        }
      });
    }

    /**
     * Get recharge information.
     * 
     * @returns {String} - Recharge bundle id
     */
    async getRechargeInformation() {
      const selections = this.selectedBundleCards.map((bundleCard) => {
        return {
          ...{
            collectionId: bundleCard.collection,
            externalVariantId: bundleCard.variantId,
            externalProductId: bundleCard.productId,
            quantity: bundleCard.quantity,
          }
        }
      });

      const bundleId = await window.recharge.bundle.getBundleId(
        {
          externalVariantId: this.variantId,
          externalProductId: this.productId,
          selections
        }
      )
      return bundleId;
    }

    /**
     * Add to cart.
     * 
     * @param {Event} event 
     */
    async addToCart(event) {
      event.preventDefault();
      this.handlePlanChange();
      const rechargeId = this.isDynamic ? null : (await this.getRechargeInformation());
      const items = rechargeId === null ? [
        // {
        //   id: this.variantId,
        //   quantity: 1,
        //   properties: {
        //     contents: this.selectedBundleCards.map((bundleCard) => {
        //       return `${bundleCard.title} x${bundleCard.quantity}`;
        //     }).join(', ')
        //   }
        // }
        ...this.bundleCardsData
      ] : [
        {
          id: this.variantId,
          quantity: 1,
          selling_plan: this.sellingPlan,
          properties: {
            _rb_id: rechargeId,
            contents: this.selectedBundleCards.map((bundleCard) => {
              return `${bundleCard.title} x${bundleCard.quantity}`;
            }).join(', ')
          }
        }
      ];

      for (const addonProduct of this.selectedAddonProducts.map(addon => addon.variantId)) {
        items.push({
          id: addonProduct,
          quantity: 1,
          properties: { "Bundle": this.tag }
        }); 
      }

      const groupId = new Date().getTime();

      items.forEach(item => {
        item.properties = {
          ...item.properties,
          "_group_id": groupId,
          "Bundle": this.tag
        }
      });

      
      const formId = this.getAttribute('id');
      const config = fetchConfigDefaults("application/javascript");
      const formData = new FormData();
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
        if (item.properties) {
          for (const [key, value] of Object.entries(item.properties)) {
            formData.append(`items[${index}][properties][${key}]`, value);
          }
        }
        if (item.selling_plan) {
          formData.append(`items[${index}][selling_plan]`, item.selling_plan);
        }
      })
      let modalCart = theme.settings.cartType === "modal";
      if (modalCart) {
        formData.append("sections", "cart-items,cart-footer,cart-item-count");
        formData.append("sections_url", window.location.pathname);
      }
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      config.body = formData;
      fetch(`${theme.routes.cart_add_url}`, config)
        .then((res) => res.json())
        .then((data) => {
          if (data.status) {
            return;
          }
          if (modalCart) {
            document.body.dispatchEvent(
              new CustomEvent("shapes:cart:afteradditem", {
                bubbles: true,
                detail: { response: data, sourceId: formId }
              })
            );
            
            this.buyButtons.forEach(button => {
              button.closest('mobile-slide-out')?.hide();
            });
          }
        })
        .catch(error => {
          console.error(error)
        });
    }
    
    /**
     * Add listeners.
     */
    addListeners() {
      this.wrapper.addEventListener('bundle:addon:update', this.handleAddonChange.bind(this));
      this.wrapper.addEventListener('bundle:quantity:update', this.handleQuantityUpdate.bind(this));
      this.wrapper.addEventListener('mobile-slide-out:open', this.handleSlideOutOpen.bind(this));
      this.addEventListener('change', this.handlePlanChange.bind(this));
      this.buyButtons.forEach(button => {
        if (button.hasAttribute('no-add-to-cart')) {
          return;
        }
        button.addEventListener('click', this.addToCart.bind(this));
      });
    }
    
    /**
     * Connected callback.
     */
    connectedCallback() {
      this.wrapper = this.closest(this.selectors.wrapper);
      if (!this.wrapper) {
        console.error('No wrapper found for bundle product');
      }
      this.bundleCollections = this.wrapper.querySelectorAll(this.selectors.bundleCollection);
      this.buyButtons = [...this.wrapper.querySelectorAll(`${this.selectors.buyButton}[data-id="${this.getAttribute('id')}"], [no-add-to-cart]`)];
      this.mobileSlideOuts = this.wrapper.querySelectorAll(this.selectors.mobileSlideOuts);
      this.summary = this.wrapper.querySelector(this.selectors.summary);
      this.items = this.wrapper.querySelectorAll(this.selectors.items);
      this.getRechargeData();
      this.addListeners();
      this.updateErrorMessages();
      this.tag = this.getAttribute('data-tag') || '';
    }
  }

  customElements.define('bundle-product', BundleProduct);
})();